import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { authAPI, getApiBaseUrl, joinLinksAPI } from '../services/api'
import { RithmoWordmark } from './shell/OrgMark'
import { TONE_CLASS } from './shell/agentTones'
import ConnectAssistantSteps from './ConnectAssistantSteps'
import VerifyCodeInline from './VerifyCodeInline'

/**
 * /join?link=<token> — the company join link an admin shares ("Join Acme and connect your agent"). Shows the company,
 * signs the person in or up right here (Google, Microsoft, or name + email + password: no company questions), joins them
 * to that existing company as a member, then shows how to connect their assistant. A verified account joins (v2 R1): after
 * an email signup the person enters the emailed code here (or uses the email's Confirm button) and the join completes on
 * verification; Google/Microsoft accounts join at once.
 */

const PROVIDER_VALUE = { google: 'GoogleOAuth', microsoft: 'MicrosoftOAuth' }
const SSO_LABEL = { google: 'Google', microsoft: 'Microsoft' }
const inputCls = 'mt-2 w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-primary-400'
const primaryBtn = 'mt-6 flex w-full items-center justify-center rounded-xl bg-primary-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60'
const secondaryBtn = 'flex w-full items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-50'

const passwordOk = (p) => p.length >= 8 && /\d/.test(p) && /[A-Z]/.test(p)

function Layout({ company, children }) {
  return (
    <div className="grid min-h-screen grid-cols-1 bg-gray-50 lg:grid-cols-2">
      <div className="hidden flex-col justify-between bg-brand-gradient p-12 text-white lg:flex">
        <RithmoWordmark size={36} onDark />
        <div className="max-w-md">
          <h2 className="text-3xl font-bold leading-tight">{company ? `Join ${company} on Rithmo.` : 'Join your company on Rithmo.'}</h2>
          <p className="mt-4 text-base leading-relaxed text-white/75">Then connect your AI assistant so it checks your company&apos;s business context before it acts.</p>
        </div>
        <p className="text-sm text-white/60">You join as a member. Your company&apos;s admins see the agents you connect.</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full min-w-0 max-w-sm" data-join>
          <div className="mb-8 lg:hidden"><RithmoWordmark size={36} /></div>
          {children}
        </div>
      </div>
    </div>
  )
}

export default function CustomerJoinPage() {
  const [params] = useSearchParams()
  const link = (params.get('link') || '').trim()
  const { isAuthenticated, emailVerified, loading: authLoading, user, signup, logout } = useAuth()
  const [preview, setPreview] = useState({ status: 'loading' })
  const [joined, setJoined] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [ssoMethods, setSsoMethods] = useState([])
  const [form, setForm] = useState({ name: '', email: '', password: '' })

  useEffect(() => {
    let cancelled = false
    joinLinksAPI.preview(link)
      .then((r) => { if (!cancelled) setPreview({ status: r?.status ?? 'missing', company: r?.company }) })
      .catch(() => { if (!cancelled) setPreview({ status: 'missing' }) })
    authAPI.getSsoMethods()
      .then((r) => { if (!cancelled) setSsoMethods((r?.data?.methods ?? r?.methods ?? []).filter((m) => m === 'google' || m === 'microsoft')) })
      .catch(() => { if (!cancelled) setSsoMethods([]) })
    return () => { cancelled = true }
  }, [link])

  const accept = useCallback(async () => {
    setBusy(true); setError('')
    try {
      const r = await joinLinksAPI.accept(link)
      setJoined({ name: r?.company?.name ?? preview.company?.name })
    } catch (err) {
      setError(err?.status === 410 ? 'This join link has expired or was turned off. Ask your admin for a new one.' : 'Could not join right now. Try again.')
    } finally { setBusy(false) }
  }, [link, preview.company])

  // Signed in with a verified account (already, or back from Google/Microsoft): join straight away. An unconfirmed account
  // confirms its email below first.
  useEffect(() => {
    if (!authLoading && isAuthenticated && emailVerified && preview.status === 'active' && !joined && !busy && !error) accept()
  }, [authLoading, isAuthenticated, emailVerified, preview.status, joined, busy, error, accept])

  // Verified here: the server joins a pending signup itself (`joined`); otherwise accept the link now.
  const onVerified = (res) => {
    if (res?.joined) setJoined({ name: res.joined.company?.name ?? preview.company?.name })
    else accept()
  }

  const startSso = (method) => {
    const base = getApiBaseUrl().replace(/\/+$/, '')
    window.location.href = `${base}/auth/sso/start?provider=${encodeURIComponent(PROVIDER_VALUE[method])}&return_to=${encodeURIComponent(`/join?link=${link}`)}`
  }

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    if (!passwordOk(form.password)) { setError('Use at least 8 characters with a number and a capital letter.'); return }
    setBusy(true)
    try {
      // The account is created and the link waits for the email confirmation (the code step below).
      await signup(form.email.trim(), form.password, form.name.trim(), { join_link: link })
    } catch (err) {
      setError(err?.message || 'Could not create your account. Try again.')
    } finally { setBusy(false) }
  }

  const company = preview.company?.name
  if (preview.status === 'loading' || authLoading) return <Layout company={company}><p className="text-sm text-gray-500" role="status">Loading…</p></Layout>
  if (preview.status !== 'active') {
    return (
      <Layout>
        <h1 className="text-2xl font-bold text-gray-900">{preview.status === 'missing' ? 'This link does not work' : 'This link has expired'}</h1>
        <p className="mt-2 text-sm text-gray-500">Ask your company&apos;s Rithmo admin for a new join link.</p>
      </Layout>
    )
  }

  if (joined) {
    return (
      <Layout company={company}>
        <h1 className="text-2xl font-bold text-gray-900">You&apos;re in {joined.name || company}.</h1>
        <p className="mt-2 text-sm text-gray-500">Now connect your AI assistant. It takes a few seconds.</p>
        <div className="mt-6"><ConnectAssistantSteps /></div>
        <p className="mt-6 text-center text-sm text-gray-500"><Link to="/my-agents" className="font-semibold text-primary-700 hover:text-primary-800">Go to My agents</Link></p>
      </Layout>
    )
  }

  if (isAuthenticated && !emailVerified) {
    return (
      <Layout company={company}>
        <h1 className="text-2xl font-bold text-gray-900">Confirm your email</h1>
        <p className="mt-2 text-sm text-gray-500">Then you join {company}.</p>
        <VerifyCodeInline onVerified={onVerified} />
        {error && <div role="alert" className={`mt-6 rounded-xl border p-3 text-sm ${TONE_CLASS.alert}`}>{error}</div>}
      </Layout>
    )
  }

  if (isAuthenticated) {
    return (
      <Layout company={company}>
        <h1 className="text-2xl font-bold text-gray-900">Joining {company}…</h1>
        {error && <div role="alert" className={`mt-6 rounded-xl border p-3 text-sm ${TONE_CLASS.alert}`}>{error}</div>}
        <p className="mt-6 text-center text-sm text-gray-500">
          Signed in as <span className="font-medium text-gray-700">{user?.email}</span>{' · '}
          <button type="button" onClick={logout} className="font-semibold text-primary-700">Not you?</button>
        </p>
      </Layout>
    )
  }

  return (
    <Layout company={company}>
      <h1 className="text-2xl font-bold text-gray-900">Join {company}</h1>
      <p className="mt-2 text-sm text-gray-500">Sign in or create your account. You join as a member.</p>
      {ssoMethods.length > 0 && (
        <div className="mt-6 space-y-3">
          {ssoMethods.map((m) => <button key={m} type="button" onClick={() => startSso(m)} className={secondaryBtn}>Continue with {SSO_LABEL[m]}</button>)}
          <div className="flex items-center gap-3 pt-1"><div className="h-px flex-1 bg-gray-300" /><span className="text-xs text-gray-600">or with email</span><div className="h-px flex-1 bg-gray-300" /></div>
        </div>
      )}
      <form onSubmit={submit} className="mt-2">
        <label htmlFor="join-name" className="mt-4 block text-sm font-semibold text-gray-900">Your name</label>
        <input id="join-name" required autoComplete="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
        <label htmlFor="join-email" className="mt-4 block text-sm font-semibold text-gray-900">Work email</label>
        <input id="join-email" type="email" required autoComplete="username" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
        <label htmlFor="join-password" className="mt-4 block text-sm font-semibold text-gray-900">Password</label>
        <input id="join-password" type="password" required autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
        <p className="mt-1 text-xs text-gray-500">8+ characters, a number and a capital letter.</p>
        {error && <div role="alert" className={`mt-4 rounded-xl border p-3 text-sm ${TONE_CLASS.alert}`}>{error}</div>}
        <button type="submit" disabled={busy} className={primaryBtn}>{busy ? 'Joining…' : `Join ${company}`}</button>
      </form>
      <p className="mt-4 text-center text-sm text-gray-500">
        Already have an account? <Link to={`/login`} state={{ from: { pathname: '/join', search: `?link=${link}` } }} className="font-semibold text-primary-700 hover:text-primary-800">Sign in</Link>
      </p>
    </Layout>
  )
}
